import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const PIN_ZOOM = 17;

/**
 * OpenStreetMap preview for franchise store GPS coordinates.
 * Click/drag adjusts the pin when interactive.
 */
export default function StoreLocationMap({
  latitude,
  longitude,
  interactive = true,
  onChange,
  heightClass = "h-[280px]",
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const hasCoords =
    typeof latitude === "number" &&
    Number.isFinite(latitude) &&
    typeof longitude === "number" &&
    Number.isFinite(longitude);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return undefined;

    const start = hasCoords ? [latitude, longitude] : DEFAULT_CENTER;
    const zoom = hasCoords ? PIN_ZOOM : DEFAULT_ZOOM;
    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
      dragging: interactive,
      scrollWheelZoom: interactive,
    }).setView(start, zoom);
    map.attributionControl.setPrefix("");
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const emit = (lat, lng) => {
      onChangeRef.current?.({
        latitude: Number(lat.toFixed(7)),
        longitude: Number(lng.toFixed(7)),
      });
    };

    if (hasCoords) {
      markerRef.current = L.marker(start, { draggable: interactive }).addTo(map);
      if (interactive) {
        markerRef.current.on("dragend", () => {
          const pos = markerRef.current.getLatLng();
          emit(pos.lat, pos.lng);
        });
      }
    }

    if (interactive) {
      map.on("click", (e) => {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
          markerRef.current.on("dragend", () => {
            const pos = markerRef.current.getLatLng();
            emit(pos.lat, pos.lng);
          });
        }
        emit(lat, lng);
      });
    }

    mapInstanceRef.current = map;
    const t = window.setTimeout(() => map.invalidateSize(), 120);
    return () => {
      window.clearTimeout(t);
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (interactive) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
    } else {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
    }
    if (markerRef.current) {
      if (interactive) markerRef.current.dragging?.enable();
      else markerRef.current.dragging?.disable();
    }
  }, [interactive]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !hasCoords) return;
    const latLng = [latitude, longitude];
    if (markerRef.current) {
      const cur = markerRef.current.getLatLng();
      if (Math.abs(cur.lat - latitude) > 1e-7 || Math.abs(cur.lng - longitude) > 1e-7) {
        markerRef.current.setLatLng(latLng);
      }
      if (interactive) markerRef.current.dragging?.enable();
      else markerRef.current.dragging?.disable();
    } else {
      markerRef.current = L.marker(latLng, { draggable: interactive }).addTo(map);
      markerRef.current.on("dragend", () => {
        const pos = markerRef.current.getLatLng();
        onChangeRef.current?.({
          latitude: Number(pos.lat.toFixed(7)),
          longitude: Number(pos.lng.toFixed(7)),
        });
      });
    }
    map.setView(latLng, Math.max(map.getZoom(), PIN_ZOOM));
  }, [hasCoords, latitude, longitude, interactive]);

  return (
    <div className={`overflow-hidden rounded-lg border border-white/20 bg-white ${heightClass}`}>
      <div ref={mapRef} className="h-full w-full z-0" />
    </div>
  );
}
